import { useEffect, useState, useCallback } from 'react';
import {
  AssistantSessionSortKey,
  SortDirection,
  User,
  ContentSortKey,
  ContentState,
} from '@studio/graphql-types';
import { MainNavigation } from '@studio/navigation';
import { Assistant, AssistantSession, MessageType } from '@studio/assistant';
import { Icon, NavigationMenu, toast } from '@appcues/sonar';
import { NavLink, useLocation } from 'react-router-dom';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { useAuth, useGraphQLMutation, useGraphQLQuery } from 'studio5/contexts';
import { useFeature } from 'studio5/hooks';
import { useAgentMemory } from 'studio5/hooks/use-agent-memory';
import useDebounce from 'studio5/hooks/use-debounce';
import {
  ASSISTANT_SESSIONS,
  GET_ASSISTANT_SESSION_MESSAGES,
  DELETE_ASSISTANT_SESSION,
  GET_USER_ACCOUNT,
  GET_CONTENTS,
} from './queries';
import {
  GetContentsQuery,
  GetContentsQueryVariables,
  AssistantSessionsQuery,
  AssistantSessionsQueryVariables,
  DeleteAssistantSessionMutation,
  DeleteAssistantSessionMutationVariables,
  GetUserAccountQuery,
  GetAssistantSessionMessagesQuery,
  GetAssistantSessionMessagesQueryVariables,
} from './__generated__/queries.types';
import { AssistantButton } from './NavigationProvider.styled';

const NavigationProvider = () => {
  const hasOpenBuilderFlows = useFeature('OPEN_BUILDER_FLOWS');
  const hasBanners = useFeature('banners');
  const hasLaunchpads = useFeature('LAUNCHPADS_V2');
  const hasNps2 = useFeature('NPS_2_0');
  const hasOverviewRedirect = useFeature('OVERVIEW_REDIRECT');
  const hasAiAssistant = useFeature('AI_ASSISTANT');
  const hasNav2025 = useFeature('NAV_2025');
  const hasAssistantSessions = useFeature('ASSISTANT_SESSIONS');
  const hasEmbeds = useFeature('EMBEDDED_FLOWS');
  const hasAiInsights = useFeature('AI_INSIGHTS');
  const hasFunnels = useFeature('FUNNELS');

  const { memoryContent, memoryUpdatedAt, memoryFetching, fetchMemory } =
    useAgentMemory();
  const { isSpoofing, spoof, logout, client } = useAuth();
  const [token, setToken] = useState<string>();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [contentsSearch, setContentsSearch] = useState<string>('');
  const debouncedContentsSearch = useDebounce(contentsSearch, 300);

  useEffect(() => {
    client.getToken().then(setToken);
  }, [client]);

  const [{ fetching, data }] = useGraphQLQuery<GetUserAccountQuery>({
    query: GET_USER_ACCOUNT,
  });
  const { account, user } = data?.identity || {};
  const { isWebInstalled, isMobileInstalled } = data?.trialStatus || {};

  const [
    { fetching: fetchingAssistantSessions, data: assistantSessionsData },
    fetchAssistantSessions,
  ] = useGraphQLQuery<AssistantSessionsQuery, AssistantSessionsQueryVariables>({
    query: ASSISTANT_SESSIONS,
    variables: {
      sort: {
        key: AssistantSessionSortKey.UpdatedAt,
        direction: SortDirection.Desc,
      },
    },
    requestPolicy: 'network-only',
  });

  const hasCaptainAiAnalytics = account?.aiAllowEndUserData;

  const [
    {
      fetching: fetchingAssistantSessionMessages,
      data: assistantSessionMessagesData,
    },
    fetchAssistantSessionMessages,
  ] = useGraphQLQuery<
    GetAssistantSessionMessagesQuery,
    GetAssistantSessionMessagesQueryVariables
  >({
    query: GET_ASSISTANT_SESSION_MESSAGES,
    requestPolicy: 'network-only',
    pause: !sessionId,
    variables: {
      id: sessionId ?? '',
    },
  });

  const [{ data: contentsData }] = useGraphQLQuery<
    GetContentsQuery,
    GetContentsQueryVariables
  >({
    query: GET_CONTENTS,
    variables: {
      filter: {
        term: debouncedContentsSearch,
        states: [ContentState.Draft, ContentState.Published],
      },
      pageInfo: {
        limit: 1000,
        offset: 0,
      },
      sort: [
        {
          direction: SortDirection.Asc,
          key: ContentSortKey.Name,
        },
      ],
    },
  });

  const onContentSearch = useCallback(
    (search: string) => {
      setContentsSearch(search);
    },
    [setContentsSearch]
  );

  const handleSessionSelect = (id: string) => {
    setSessionId(id);
    fetchAssistantSessionMessages();
  };

  const [, deleteSession] = useGraphQLMutation<
    DeleteAssistantSessionMutation,
    DeleteAssistantSessionMutationVariables
  >(DELETE_ASSISTANT_SESSION);

  const handleDeleteSession = async (id: string) => {
    try {
      const result = await deleteSession({ id });
      if (result.data?.deleteAssistantSession?.success) {
        fetchAssistantSessions();
        toast.success('Session deleted');
      } else {
        toast.danger('Failed to delete session');
      }
    } catch (error) {
      console.error(error);
      toast.danger('Failed to delete session');
    }
  };

  const sessions = assistantSessionsData?.assistantSessions?.sessions || [];

  const navigationFeatureFlags = {
    hasOpenBuilderFlows,
    hasBanners,
    hasLaunchpads,
    hasNps2,
    hasDevTools: user?.isAdmin,
    hasOverviewRedirect,
    hasAiAssistant,
    hasNav2025,
    hasEmbeds,
    hasAiInsights,
    hasFunnels,
  };

  const { pathname } = useLocation();

  if (fetching) {
    return null;
  }

  return (
    <MainNavigation
      /* This is an inconsistency between GraphQL <> REST (identity) */
      user={{ ...user, fullName: user?.fullname } as User}
      account={account}
      trialStatus={data?.trialStatus || {}}
      accountsCount={user?.userAccounts?.length || 0}
      onSpoof={spoof}
      onLogout={logout}
      isSpoofing={isSpoofing}
      NavLink={NavLink}
      isInstalled={Boolean(isWebInstalled) || Boolean(isMobileInstalled)}
      featureFlags={navigationFeatureFlags}
      cannyAppId={import.meta.env.CANNY_APP_ID}
      currentPath={pathname}
      actions={
        <Assistant
          contents={contentsData?.contents}
          onContentSearch={onContentSearch}
          user={{
            fullName: user?.fullname ?? '',
            avatarUrl: user?.avatarUrl ?? '',
          }}
          jwt={token ?? ''}
          url={import.meta.env.CUSTOMER_API_URL ?? ''}
          sessions={sessions as AssistantSession[]}
          fetchingSessions={fetchingAssistantSessions}
          getSessionMessages={handleSessionSelect}
          refreshSession={() => {
            setSessionId(null);
            fetchAssistantSessions();
          }}
          defaultMessages={
            (assistantSessionMessagesData?.assistantSession?.messages ||
              []) as MessageType[]
          }
          fetchingAssistantSessionMessages={fetchingAssistantSessionMessages}
          hasAssistantSessions={Boolean(hasAssistantSessions)}
          hasAnalytics={Boolean(hasCaptainAiAnalytics)}
          onDeleteSession={handleDeleteSession}
          config={{
            mobileBuilderUrl: import.meta.env.MOBILE_BUILDER_URL,
          }}
          memoryContent={memoryContent}
          memoryUpdatedAt={memoryUpdatedAt}
          memoryFetching={memoryFetching}
          onFetchMemory={fetchMemory}
          settingsUrl="/settings/ai-assistant"
        >
          <NavigationMenu.Item>
            <AssistantButton>
              <Icon icon={faSparkles} colorToken="foreground-link" />
              Captain AI
            </AssistantButton>
          </NavigationMenu.Item>
        </Assistant>
      }
    />
  );
};

export default NavigationProvider;
